import urlResolver from 'edrolo/utils/URLResolver';
import Cookies from 'js-cookie';
import { debounce } from 'lodash';
import { useCallback, useMemo } from 'react';
import useSWR from 'swr';
import { Task, TaskItem, TaskLifecycleStageAction } from '../types/Task';
import { performTeacherTaskAction } from './performTeacherTaskAction';
import { post } from 'core/utils/request';

export const useTaskTeacher = (taskId: Task['id']) => {
  const singleTaskUrl = urlResolver.reverse('api:tasks-detail', { taskId });
  const tasksUrl = urlResolver.reverse('api:tasks');
  const csrfToken: string = Cookies.get('csrftoken') || '';

  const { data, mutate, error, isLoading } = useSWR<Task>(singleTaskUrl);

  const {
    data: tasks,
    mutate: mutateTasks,
    isLoading: tasksLoading,
  } = useSWR<Task[]>(tasksUrl);

  const update = useCallback(
    async (task: Task, optimistic = true) => {
      try {
        mutateTasks(
          (tasks: Task[] | undefined) => {
            if (!tasks || !Array.isArray(tasks)) {
              return [task];
            }
            if (tasks.length === 0) {
              return [task];
            }
            return tasks.map((t) => (t.id === task.id ? task : t));
          },
          { revalidate: false }
        );

        // Add sort_order immediately prior to persisting to the server, as it's
        // easier than keeping it up to date in the component state.
        const taskWithSortOrder: Partial<Task> = {
          ...task,
          task_items: task.task_items?.map((taskItem, i) => ({
            ...taskItem,
            sort_order: i,
          })) as TaskItem[],
        };

        return mutate(
          async () => {
            const response = await fetch(singleTaskUrl, {
              method: 'PATCH',
              body: JSON.stringify(taskWithSortOrder),
              headers: {
                'Content-Type': 'application/json',
                'X-CSRFToken': csrfToken,
              },
            });
            if (!response.ok) {
              throw new Error('Network response was not ok');
            }
            return response.json();
          },
          {
            optimisticData: optimistic ? task : undefined,
            revalidate: false,
          }
        );
      } catch (error) {
        console.error('There was an error updating the tasks list', error);
        if (tasks) {
          mutateTasks(tasks, { revalidate: false });
        }
        return;
      }
    },
    [singleTaskUrl, mutate, csrfToken, mutateTasks, tasks]
  );

  const duplicate = async (): Promise<Task | undefined> => {
    const task = data;

    if (!task) {
      return undefined;
    }

    try {
      const duplicatedTask = await post(
        urlResolver.reverse('api:tasks-duplicate', { taskId: task.id })
      );

      // Add the duplicated task to the list cache
      mutateTasks(
        (tasks: Task[] | undefined) => {
          if (!tasks || !Array.isArray(tasks)) {
            return [duplicatedTask];
          }
          if (tasks.length === 0) {
            return [duplicatedTask];
          }
          return [duplicatedTask, ...tasks];
        },
        { revalidate: false }
      );

      return duplicatedTask;
    } catch (error) {
      console.error('Failed to duplicate task:', error);
      throw error;
    }
  };

  const performTaskAction = useCallback(
    (action: TaskLifecycleStageAction) => {
      if (!data) {
        return;
      }

      return performTeacherTaskAction(action, {
        task: data,
        csrfToken,
        mutate,
        mutateTasks,
        tasks,
      });
    },
    [data, mutate, mutateTasks, csrfToken, tasks]
  );

  return {
    data,
    debouncedUpdate: useMemo(() => debounce(update, 300), [update]),
    update,
    duplicate,
    performTaskAction,
    mutate,
    isLoading: isLoading || tasksLoading,
    error,
  };
};
